import { produce } from "immer";
import type {
  TriviaConfig,
  TriviaHelp,
  TriviaPhase,
  TriviaSelection,
} from "./trivia-types";

export type TriviaMachineState = {
  phase: TriviaPhase;
  seed: number;
  questionIndex: number;
  selections: TriviaSelection[];
  usedHelp: TriviaHelp[];
  submittedQuestionId?: string;
};

export type TriviaAction =
  | { type: "start" }
  | {
      type: "answer";
      questionId: string;
      answerId: string;
      correct: boolean;
      points: number;
      totalQuestions: number;
      skipFeedback: boolean;
    }
  | {
      type: "timeout";
      questionId: string;
      totalQuestions: number;
      skipFeedback: boolean;
    }
  | { type: "advance"; totalQuestions: number }
  | { type: "help"; help: TriviaHelp };

export function createInitialTriviaMachineState(
  config: TriviaConfig,
  seed: number,
): TriviaMachineState {
  return {
    phase:
      config.welcome.enabled && config.questions.length > 0
        ? "welcome"
        : "question",
    seed,
    questionIndex: 0,
    selections: [],
    usedHelp: [],
    submittedQuestionId: undefined,
  };
}

function advance(draft: TriviaMachineState, totalQuestions: number) {
  draft.questionIndex += 1;
  draft.submittedQuestionId = undefined;
  // usedHelp is per-question state (its only consumer, TriviaQuestion's
  // hiddenAnswerIds derivation, re-derives it fresh per question), so reset it here.
  // A future "once per game" help type would need its own field on TriviaMachineState
  // rather than reusing usedHelp, since this reset would clear that too.
  draft.usedHelp = [];
  draft.phase = draft.questionIndex >= totalQuestions ? "result" : "question";
}

function submit(
  draft: TriviaMachineState,
  questionId: string,
  selection: TriviaSelection,
  totalQuestions: number,
  skipFeedback: boolean,
) {
  // Guards the case a countdown's onEnd fires more than once (identity churn on an inline
  // callback) or a fast double-tap dispatches twice before the phase flip unmounts the buttons.
  if (draft.submittedQuestionId === questionId) {
    return;
  }

  draft.submittedQuestionId = questionId;
  draft.selections.push(selection);

  if (skipFeedback) {
    advance(draft, totalQuestions);
  } else {
    draft.phase = "feedback";
  }
}

export function triviaReducer(
  state: TriviaMachineState,
  action: TriviaAction,
): TriviaMachineState {
  return produce(state, (draft) => {
    switch (action.type) {
      case "start":
        if (draft.phase === "welcome") {
          draft.phase = "question";
        }
        break;
      case "answer":
        submit(
          draft,
          action.questionId,
          {
            questionId: action.questionId,
            answerId: action.answerId,
            correct: action.correct,
            points: action.points,
          },
          action.totalQuestions,
          action.skipFeedback,
        );
        break;
      case "timeout":
        submit(
          draft,
          action.questionId,
          {
            questionId: action.questionId,
            answerId: undefined,
            correct: false,
            points: 0,
          },
          action.totalQuestions,
          action.skipFeedback,
        );
        break;
      case "advance":
        advance(draft, action.totalQuestions);
        break;
      case "help":
        if (!draft.usedHelp.includes(action.help)) {
          draft.usedHelp.push(action.help);
        }
        break;
      default:
        break;
    }
  });
}
